import _ from 'lodash';
import VALIDATION from '@/utils/validationFields';
import {
  ETrigger,
  IStateInventory,
  TInventoryData,
  Placement,
  IWidgetParams,
} from '@/types/publisher/inventory';
import {
  typeTraffic,
  typeDevice,
  ErrorsInterface,
  ResponseData,
} from '@/types';

function clearErrors(errors: ErrorsInterface): void {
  _.forEach(errors, (value: Array<string | undefined>): void => {
    value.splice(0, value.length);
  });
}

export function validateInventoryForm(inventory: IStateInventory): boolean {
  let isValid = true;
  clearErrors(inventory.errors);
  VALIDATION.required(inventory.name, inventory.errors.name);
  VALIDATION.required(inventory.domain, inventory.errors.domain);

  const errors = _.every(inventory.errors, (error) => error.length === 0);
  isValid = errors ? isValid : errors;

  return isValid;
}

export function validatePlacementsForm(placement: Placement): boolean {
  let isValid = true;

  clearErrors(placement.errors);
  VALIDATION.required(placement.name, placement.errors.name);

  const errors = _.every(placement.errors, (error) => error.length === 0);
  isValid = errors ? isValid : errors;

  return isValid;
}

export function validatePlacementWidgetParams(
  widgetParams: IWidgetParams
): boolean {
  let isValid = true;

  clearErrors(widgetParams.errors);

  // Banner
  if (
    widgetParams.type === typeTraffic.BANNER ||
    widgetParams.type === typeTraffic.NATIVE
  ) {
    VALIDATION.required(widgetParams.element, widgetParams.errors.element);
  }
  if (widgetParams.type === typeTraffic.BANNER) {
    VALIDATION.required(widgetParams.width, widgetParams.errors.width);
    VALIDATION.required(widgetParams.height, widgetParams.errors.height);
  }

  // Native
  if (widgetParams.type === typeTraffic.NATIVE) {
    VALIDATION.required(widgetParams.cols, widgetParams.errors.cols);
    VALIDATION.required(widgetParams.rows, widgetParams.errors.rows);
    VALIDATION.numberMoreZero(widgetParams.cols, widgetParams.errors.cols);
    VALIDATION.numberMoreZero(widgetParams.rows, widgetParams.errors.rows);

    if (widgetParams.useMaxHeight) {
      VALIDATION.required(
        widgetParams.maxHeight,
        widgetParams.errors.maxHeight
      );
      VALIDATION.numberMoreZero(
        widgetParams.maxHeight,
        widgetParams.errors.maxHeight
      );
    }
  }

  // Push
  if (widgetParams.type === typeTraffic.PUSH) {
    if (widgetParams.typeDevice === typeDevice.MOBILE) {
      VALIDATION.numberMoreEqualZero(
        widgetParams.mobileVerticalMargin,
        widgetParams.errors.mobileVerticalMargin
      );
    } else {
      VALIDATION.numberMoreEqualZero(
        widgetParams.desktopVerticalMargin,
        widgetParams.errors.desktopVerticalMargin
      );
      VALIDATION.numberMoreEqualZero(
        widgetParams.desktopHorizontalMargin,
        widgetParams.errors.desktopHorizontalMargin
      );
    }
    if (
      widgetParams.openTriggerType === ETrigger.ANCHOR_CLASS ||
      widgetParams.openTriggerType === ETrigger.ANCHOR_ID ||
      widgetParams.openTriggerType === ETrigger.SCROLL
    ) {
      VALIDATION.required(
        widgetParams.openTriggerValue,
        widgetParams.errors.openTriggerValue
      );
    }
    if (widgetParams.openTriggerType === ETrigger.SCROLL) {
      VALIDATION.numberMore0Less100(
        widgetParams.openTriggerValue,
        widgetParams.errors.openTriggerValue
      );
    }
    if (widgetParams.useCloseTrigger) {
      if (widgetParams.closeTriggerType === ETrigger.SCROLL) {
        VALIDATION.required(
          widgetParams.closeTriggerValue,
          widgetParams.errors.closeTriggerValue
        );
        VALIDATION.numberMore0Less100(
          widgetParams.closeTriggerValue,
          widgetParams.errors.closeTriggerValue
        );
      }
    }
  }

  const errors = _.every(widgetParams.errors, (error) => error.length === 0);
  isValid = errors ? isValid : errors;

  return isValid;
}

export function setPublisherErrorsFromResponseData(
  errors: ResponseData[],
  inventoryData: TInventoryData
): void {
  if (errors?.length) {
    const { inventory, placements, placementWidgetParams } = inventoryData;

    _.forEach(errors, (error) => {
      if (error.field.includes('placements')) {
        const errorIndex = Number(error.field.replace(/\D/g, ''));

        if (!isNaN(errorIndex)) {
          _.forEach(placements.allIds, (id: string, pIndex: number) => {
            if (errorIndex === pIndex) {
              _.forEach(error.message, (value, key) => {
                if (key === 'name') {
                  placements.byId[id].errors[key].push(value);
                } else {
                  placementWidgetParams.byId[id].errors[key].push(value);
                }
              });
            }
          });
        }
        return;
      }
      inventory.errors[error.field].push(error.message);
    });
  }
}
